import { TFunctionDetailedResult } from 'i18next'
import { i18n } from 'i18n/i18n'

/** Error messages of the exchangeTransferCalculate endpoint, compared verbatim. */
export enum ApiErrorMessage {
  CurrencyNotSupported = 'Currency not supported',
  CurrencyCountryPairNotSupported = 'Currency country pair not supported',
}

export const isCurrencyNotSupported = (apiErrorMessage: string): boolean =>
  apiErrorMessage === ApiErrorMessage.CurrencyNotSupported

/** True once the calculated amount actually reaching the recipient (after all fees) is not positive anymore. */
export const isAmountTooLowForFees = (receivedAmount: string): boolean => Number(receivedAmount) <= 0

/**
 * Red border on the amount input: the API rejected the currency, the amount is not positive, or — for the
 * sent amount only — it's positive but too small to leave anything for the recipient after all fees.
 */
export const shouldShowAmountError = (
  apiErrorMessage: string,
  amount: string,
  receivedAmount: string,
  isBaseCurrencyComponent: boolean,
  isCalculationLoading: boolean,
): boolean => {
  if (isCurrencyNotSupported(apiErrorMessage)) {
    return true
  }

  if (Number(amount) <= 0) {
    return true
  }

  return isBaseCurrencyComponent && !isCalculationLoading && isAmountTooLowForFees(receivedAmount)
}

/** Message under the amount input, null when there is nothing to show. */
export const getAmountErrorMessage = (
  apiErrorMessage: string,
  amount: string,
  receivedAmount: string,
  isBaseCurrencyComponent: boolean,
  isCalculationLoading: boolean,
): TFunctionDetailedResult | null => {
  if (isCurrencyNotSupported(apiErrorMessage)) {
    return i18n('transferCalculator.noCurrenciesSupportedInfo')
  }

  if (Number(amount) <= 0) {
    return i18n('calculator.genericMinAmount')
  }

  return isBaseCurrencyComponent && !isCalculationLoading && isAmountTooLowForFees(receivedAmount)
    ? i18n('transferCalculator.amountTooLowForFees')
    : null
}

/** Message under the country select, null when the currency/country pair is supported. */
export const getCountryErrorMessage = (apiErrorMessage: string): TFunctionDetailedResult | null =>
  apiErrorMessage === ApiErrorMessage.CurrencyCountryPairNotSupported
    ? i18n('transferCalculator.noCountrySupportedInfo')
    : null
